import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '../stores/chatStore';

const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessions, isLoadingSessions, error, fetchSessions, deleteSession, setCurrentSession } = useChatStore();

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSessionClick = (sessionId: string) => {
    setCurrentSession(sessions.find((s) => s.session_id === sessionId) || null);
    navigate('/dashboard/chat');
  };

  const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('Delete this chat session?')) {
      await deleteSession(sessionId);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Chat Sessions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => useChatStore.getState().clearError()}>
          {error}
        </Alert>
      )}

      {isLoadingSessions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No chat sessions yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a new chat to begin your research
          </Typography>
        </Paper>
      ) : (
        <List>
          {sessions.map((session) => (
            <ListItem
              key={session.session_id}
              disablePadding
              secondaryAction={
                <IconButton edge="end" onClick={(e) => handleDelete(e, session.session_id)}>
                  <DeleteIcon />
                </IconButton>
              }
              sx={{ mb: 1 }}
            >
              <ListItemButton
                onClick={() => handleSessionClick(session.session_id)}
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {session.title}
                      </Typography>
                      <Chip label={formatDate(session.updated_at)} size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Created {formatDate(session.created_at)}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Container>
  );
};

export default SessionsPage;
